import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Box, Button, Divider, IconButton, Typography, Skeleton } from '@mui/material';
import Close from '@mui/icons-material/Close';
import clsx from 'clsx';
import { useCalories } from 'src/hooks/useCalories';
import { useSuggestionsContext } from 'src/hooks/useSuggestionsContext';
import { BottomDialog } from 'lib/BottomDialog';
import { QuantitySelector } from 'lib/QuantitySelector';
import { isDefined, isNumber } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import { addBasketItem, removeBasketItem, setPrepTimePadding } from 'store/basket/basketSlice';
import { ApplicationState } from 'store/store';
import { MenuProductInfoTimePaddingDialog, PrepTimeModalInfo } from '../MenuProductInfoTimePaddingDialog';
import { ESuggestionType, IEnrichedProduct, IProduct, ISuggestions, ProductGroup } from '../model/Menu';
import { showCalories } from 'lib/helpers';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { usePrepTimePadding } from 'src/hooks/usePrepTimePadding';

const PREFIX = 'MenuSuggestionsAdd';

const classes = {
    title: `${PREFIX}title`,
    closeButton: `${PREFIX}closeButton`,
    subTitle: `${PREFIX}subTitle`,
    suggestionBox: `${PREFIX}suggestionBox`,
    suggestionInfoBox: `${PREFIX}suggestionInfoBox`,
    suggestionTitle: `${PREFIX}suggestionTitle`,
    productBox: `${PREFIX}productBox`,
    productDescriptionBox: `${PREFIX}productDescriptionBox`,
    productDescription: `${PREFIX}productDescription`,
    calories: `${PREFIX}calories`,
    button: `${PREFIX}button`,
    imageSuggestionWrapper: `${PREFIX}imageSuggestionWrapper`,
    imageProductWrapper: `${PREFIX}imageProductWrapper`,
    image: `${PREFIX}image`,
    imageHidden: `${PREFIX}imageHidden`,
    quantityBox: `${PREFIX}quantityBox`,
    divider: `${PREFIX}divider`,
    skeleton: `${PREFIX}skeleton`
};

const StyledBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    position: 'relative',
    [`& .${classes.closeButton}`]: {
        position: 'absolute',
        top: 0,
        right: 0,
        padding: theme.spacing(0.5),
        color: theme.palette.text.secondary
    },
    [`& .${classes.title}`]: {
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        fontSize: '20px',
        fontWeight: theme.typography.fontWeightBold,
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.subTitle}`]: {
        marginBottom: theme.spacing(0.5),
        fontSize: '20px',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.suggestionBox}`]: {
        marginBottom: theme.spacing(2),
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.suggestionInfoBox}`]: {
        padding: theme.spacing(1.5, 2)
    },

    [`& .${classes.suggestionTitle}`]: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.productBox}`]: {
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.productDescriptionBox}`]: {
        width: '80%'
    },

    [`& .${classes.productDescription}`]: {
        opacity: '0.8'
    },

    [`& .${classes.calories}`]: {
        opacity: '0.8'
    },

    [`& .${classes.button}`]: {
        marginBottom: theme.spacing(1)
    },

    [`& .${classes.imageSuggestionWrapper}`]: {
        width: '100%',
        overflow: 'hidden'
    },

    [`& .${classes.imageProductWrapper}`]: {
        width: '60px',
        minWidth: '60px',
        height: '60px',
        marginLeft: theme.spacing(1.5),
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.image}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },

    [`& .${classes.imageHidden}`]: {
        visibility: 'hidden',
        width: 0,
        height: 0,
        padding: 0,
        margin: 0
    },

    [`& .${classes.quantityBox}`]: {
        marginBottom: theme.spacing(0.5)
    },

    [`& .${classes.divider}`]: {
        marginBottom: theme.spacing(1)
    },

    [`& .${classes.skeleton}`]: {
        height: '120px',
        width: '100%'
    }
}));

interface MenuSuggestionsAddProps {
    open: boolean;
    onClose: () => void;
    showMoreInfo: (productId: IProduct | ProductGroup | undefined, suggestionType: ESuggestionType) => void;
    currency: string;
    suggestion: ISuggestions;
    addedItem: IEnrichedProduct;
    suggestedProduct: IEnrichedProduct;
}

export const MenuSuggestionsAdd: React.FC<React.PropsWithChildren<MenuSuggestionsAddProps>> = ({
    open,
    onClose,
    showMoreInfo,
    addedItem,
    suggestion,
    suggestedProduct
}) => {
    const { title, description, imageUrl } = suggestion;
    const { key: energyKey, getProductCalories, getCaloriesString } = useCalories();
    const { logUserEvent } = useGAHelpers();

    const dispatch = useAppDispatch();
    const menu = useSelector((state: ApplicationState) => state.menu.menu);
    const categories = useSelector((state: ApplicationState) => state.menu.categories);
    const getCurrencyString = useCurrencyString();
    const { t } = useTranslation();
    const { checkPadding } = usePrepTimePadding();
    const [quantity, setQuantity] = React.useState(1);
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [imageProductLoaded, setImageProductLoaded] = React.useState(false);
    const [prepTimeModalInfo, setPrepTimeModalInfo] = React.useState<PrepTimeModalInfo | undefined>();
    const productCalories = React.useMemo(
        () => getProductCalories(suggestedProduct),
        [getProductCalories, suggestedProduct]
    );

    const context = useSuggestionsContext();
    const itemHasInfo = React.useMemo(() => {
        const prodItem = suggestedProduct;
        let hasModifiers = false;
        if (prodItem.modifiers && prodItem.modifiers.length !== 0 && menu) {
            for (const modifierId of prodItem.modifiers) {
                const categoryId = menu.productIdToCategoryId.get(prodItem.id);
                if (
                    (!!categoryId &&
                        menu.modifierIdToCategoryOptionModifierSettings.get(categoryId)?.get(modifierId)) ??
                    menu.modifierIdToOptionModifierSettings.get(modifierId) ??
                    menu.modifierIdToProductModifierSettings.get(modifierId)
                ) {
                    hasModifiers = true;
                }
            }
        }
        const nutrition =
            prodItem.nutrition &&
            prodItem.nutrition.length !== 0 &&
            prodItem.nutrition[0].values.filter(item => item.nutrient !== energyKey).length > 0;
        return (
            prodItem.imageUrl ||
            hasModifiers ||
            (prodItem.allergens && prodItem.allergens.length !== 0) ||
            (prodItem.shortDescription && prodItem.shortDescription.length !== 0) ||
            (prodItem.tags && prodItem.tags.length !== 0) ||
            nutrition
        );
    }, [suggestedProduct, menu, energyKey]);

    const handleShowMoreinfo = React.useCallback(() => {
        if (menu) {
            context.setDefaultSuggestedValue(suggestedProduct);
            if (suggestedProduct?.productGroupId) {
                const categoryId = menu.productIdToCategoryId.get(suggestedProduct.id);
                const categoryGroup = categories
                    .find(item => item.id === categoryId)
                    ?.products.find(item => item.id === suggestedProduct.productGroupId);

                if (categoryGroup) {
                    return showMoreInfo(categoryGroup, suggestion.type);
                }
            }
            const product: IProduct | undefined = menu.productIdToProduct.get(String(suggestedProduct.id));
            showMoreInfo(product, suggestion.type);
        }
    }, [categories, context, menu, showMoreInfo, suggestedProduct, suggestion.type]);

    const handleCancelPrepTimeInfo = React.useCallback(() => {
        setPrepTimeModalInfo(undefined);
        onClose();
    }, [onClose]);

    const handleAddItemToBasket = React.useCallback(() => {
        let timePadding = 0;
        let paddingBigger = false;

        const addItemToBasketFunction = () => {
            if (!menu) {
                return;
            }
            const categoryId = menu.productIdToCategoryId.get(suggestedProduct.id);
            dispatch(
                addBasketItem(suggestedProduct, categoryId || '', quantity, undefined, suggestedProduct.price)
            );
            if (paddingBigger) {
                dispatch(setPrepTimePadding(timePadding));
            }
            onClose();
        };
        if (isNumber(suggestedProduct?.prepTimeMins)) {
            timePadding = suggestedProduct.prepTimeMins;
            const isPaddingBigger = checkPadding(timePadding);

            if (isPaddingBigger) {
                paddingBigger = true;
                setPrepTimeModalInfo({
                    onSuccess: addItemToBasketFunction,
                    onCancel: handleCancelPrepTimeInfo,
                    productTitle: suggestedProduct.title,
                    timePadding
                });
                return;
            }
        }
        addItemToBasketFunction();
    }, [menu, suggestedProduct, quantity, dispatch, onClose, checkPadding, handleCancelPrepTimeInfo]);

    const handleReplaceItemInBasket = React.useCallback(() => {
        logUserEvent(EGAEventName.RelatedProductReplaceAgreeTapped);
        let timePadding = 0;
        let paddingBigger = false;

        const replaceItemFunction = () => {
            if (!menu) {
                return;
            }
            dispatch(removeBasketItem(addedItem));
            const categoryId = menu.productIdToCategoryId.get(suggestedProduct.id);
            dispatch(
                addBasketItem(
                    suggestedProduct,
                    categoryId || '',
                    1, // quantity is 1 because we are replacing the item with the new one
                    undefined,
                    suggestedProduct.price
                )
            );
            if (paddingBigger) {
                dispatch(setPrepTimePadding(timePadding));
            }
            onClose();
        };

        if (isNumber(suggestedProduct?.prepTimeMins)) {
            timePadding = suggestedProduct.prepTimeMins;
            const isPaddingBigger = checkPadding(timePadding);

            if (isPaddingBigger) {
                paddingBigger = true;
                setPrepTimeModalInfo({
                    onSuccess: replaceItemFunction,
                    onCancel: handleCancelPrepTimeInfo,
                    productTitle: suggestedProduct.title,
                    timePadding
                });
                return;
            }
        }
        replaceItemFunction();
    }, [
        logUserEvent,
        menu,
        suggestedProduct,
        dispatch,
        checkPadding,
        handleCancelPrepTimeInfo,
        onClose,
        addedItem
    ]);

    const handleSubmit = React.useCallback(() => {
        const { type } = suggestion;
        if (type === ESuggestionType.ADD) {
            handleAddItemToBasket();
        } else if (type === ESuggestionType.REPLACE) {
            handleReplaceItemInBasket();
        }
    }, [suggestion, handleAddItemToBasket, handleReplaceItemInBasket]);

    const handleCloseDialog = React.useCallback(() => {
        if (suggestion.type === ESuggestionType.REPLACE) {
            logUserEvent(EGAEventName.RelatedProductReplaceDeclineTapped);
        }
        onClose();
    }, [onClose, suggestion.type, logUserEvent]);

    const suggestedProductTitle = React.useMemo(
        () => suggestedProduct.productGroupName ?? suggestedProduct.title,
        [suggestedProduct.productGroupName, suggestedProduct.title]
    );
    const suggestedProductDescription = React.useMemo(
        () => suggestedProduct?.shortDescription ?? '',
        [suggestedProduct?.shortDescription]
    );
    const suggestionsTitle = React.useMemo(() => title ?? '', [title]);
    const suggestionsDescription = React.useMemo(() => {
        if (description && description.includes('{{product}}')) {
            return description.replace('{{product}}', suggestedProductTitle);
        }
        if (description) {
            return description;
        }
        return false;
    }, [description, suggestedProductTitle]);
    const addedProductTitle = React.useMemo(
        () =>
            isDefined(addedItem?.productGroupName)
                ? addedItem.productGroupName + ' ' + addedItem.title
                : addedItem.title,
        [addedItem]
    );
    const suggestionImage = React.useMemo(() => imageUrl ?? false, [imageUrl]);
    const handleImageLoaded = React.useCallback(() => {
        setImageLoaded(true);
    }, []);
    const suggestedProductImage = React.useMemo(
        () => suggestedProduct?.listingImageUrl || suggestedProduct?.imageUrl || false,
        [suggestedProduct?.imageUrl, suggestedProduct?.listingImageUrl]
    );
    const handleImageProductLoaded = React.useCallback(() => {
        setImageProductLoaded(true);
    }, []);

    const buttonText = React.useMemo(() => {
        const { type } = suggestion;
        if (type === ESuggestionType.REPLACE) {
            return t('RELATED_PRODUCT_REPLACE_ACCEPT', {
                price: getCurrencyString(suggestedProduct.price * quantity)
            });
        }
        return t('PREORDER_ADD_TO_BASKET', {
            price: getCurrencyString(suggestedProduct.price * quantity)
        });
    }, [suggestedProduct.price, quantity, suggestion, getCurrencyString, t]);
    return (
        <>
            <BottomDialog open={open} onClose={handleCloseDialog}>
                <StyledBox>
                    {/* TODO: consider moving this close button into BottomDialog/BaseModal as a
                        prop (e.g. showCloseButton), or even unifying BottomDialog and BaseModal */}
                    <IconButton
                        className={classes.closeButton}
                        onClick={handleCloseDialog}
                        size="small"
                        aria-label="Close"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                    {addedProductTitle && (
                        <Typography className={classes.title} align="center">
                            {addedProductTitle}
                        </Typography>
                    )}
                    <Typography className={classes.subTitle} align="center">
                        {t('RELATED_PRODUCT_ADD_CONFIRMATION')}
                    </Typography>
                    <Divider className={classes.divider} />
                    <Box className={classes.suggestionBox}>
                        {suggestionImage && (
                            <>
                                <Box
                                    className={clsx(classes.imageSuggestionWrapper, {
                                        [classes.imageHidden]: !imageLoaded
                                    })}
                                >
                                    <img
                                        className={classes.image}
                                        src={suggestionImage}
                                        onLoad={handleImageLoaded}
                                        alt={suggestionsTitle}
                                    />
                                </Box>
                                {!imageLoaded && (
                                    <Skeleton className={classes.skeleton} variant="rectangular" />
                                )}
                            </>
                        )}
                        <Box className={classes.suggestionInfoBox}>
                            <Typography className={classes.suggestionTitle} align="center">
                                {suggestionsTitle ? suggestionsTitle : t('RELATED_PRODUCT_ADD_TITLE')}
                            </Typography>
                            {suggestionsDescription && (
                                <Typography align="center">{suggestionsDescription}</Typography>
                            )}
                        </Box>
                    </Box>

                    <Box
                        className={classes.productBox}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box className={classes.productDescriptionBox}>
                            <Typography>{suggestedProductTitle}</Typography>
                            {suggestedProductDescription && (
                                <Typography className={classes.productDescription}>
                                    {suggestedProductDescription}
                                </Typography>
                            )}
                            {!itemHasInfo && productCalories.isSet && (
                                <Typography variant="body2" className={classes.calories}>
                                    {`${getCaloriesString(productCalories.value)} ${t(
                                        'ORDER_CALORIES_PER_UNIT'
                                    )}`}
                                </Typography>
                            )}
                        </Box>

                        {suggestedProductImage && (
                            <>
                                <Box
                                    className={clsx(classes.imageProductWrapper, {
                                        [classes.imageHidden]: !imageProductLoaded
                                    })}
                                >
                                    <img
                                        className={classes.image}
                                        onLoad={handleImageProductLoaded}
                                        src={suggestedProductImage}
                                        alt={suggestedProductTitle}
                                    />
                                </Box>
                                {!imageProductLoaded && (
                                    <Skeleton className={classes.imageProductWrapper} variant="rectangular" />
                                )}
                            </>
                        )}
                    </Box>

                    {itemHasInfo ? (
                        <Button color="primary" variant="contained" onClick={handleShowMoreinfo} fullWidth>
                            {t('RELATED_PRODUCT_ADD_VIEW_DETAILS')}
                        </Button>
                    ) : (
                        <>
                            {suggestion.type === ESuggestionType.ADD && (
                                <Box
                                    className={classes.quantityBox}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <QuantitySelector
                                        min={1}
                                        max={999}
                                        quantity={quantity}
                                        onChange={setQuantity}
                                    />
                                </Box>
                            )}
                            <Button
                                className={classes.button}
                                color="primary"
                                variant="contained"
                                onClick={handleSubmit}
                                fullWidth
                            >
                                {buttonText}
                            </Button>
                            {showCalories(productCalories) && (
                                <Box display="flex" alignItems="center" justifyContent="center">
                                    <Typography variant="caption" align="center">
                                        {t('ORDER_CALORIES_ADVISORY_MESSAGE')}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </StyledBox>
            </BottomDialog>
            <MenuProductInfoTimePaddingDialog
                open={!!prepTimeModalInfo}
                info={prepTimeModalInfo}
                onCancel={handleCancelPrepTimeInfo}
            />
        </>
    );
};
